import * as React from 'react'
import type { IRoute } from '@numeo/types-core'
import type { TruckLocationHistoryRow } from '../TruckHistory'
import { sortTruckHistoryByTimestampAsc } from '../utils/truckHistoryUtils'
import { formatToUserLocalTime } from '@/utils/date-formatting'

interface SelectedTrucksMapProps {
    trucks: TruckLocationHistoryRow[]
    activeVin: string | null
    focusedRow: TruckLocationHistoryRow | null
    loadRouteStops: IRoute[] | null
}

const defaultMapCenter = { lat: 39.5, lng: -104.5 }

const isValidCoordinate = (lat: number, lng: number): boolean => {
    return !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)
}

const arePositionsEqual = (a: google.maps.LatLngLiteral | undefined, b: google.maps.LatLngLiteral): boolean => {
    if (!a) return false
    const tolerance = 1e-6
    return Math.abs(a.lat - b.lat) < tolerance && Math.abs(a.lng - b.lng) < tolerance
}

const createInfoWindowContent = (timestamp: string): HTMLDivElement => {
    const contentDiv = document.createElement('div')
    contentDiv.style.padding = '8px 12px'
    contentDiv.style.backgroundColor = '#ffffff'
    contentDiv.style.borderRadius = '6px'
    contentDiv.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.18)'
    contentDiv.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    contentDiv.style.fontSize = '12px'
    contentDiv.style.color = '#0f172a'
    contentDiv.style.fontWeight = '500'
    contentDiv.style.whiteSpace = 'nowrap'
    contentDiv.textContent = timestamp
    return contentDiv
}

const createLoadStopInfoContent = (stop: IRoute): HTMLDivElement => {
    const contentDiv = document.createElement('div')
    contentDiv.style.padding = '10px 14px'
    contentDiv.style.backgroundColor = '#ffffff'
    contentDiv.style.borderRadius = '8px'
    contentDiv.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.22)'
    contentDiv.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    contentDiv.style.fontSize = '12px'
    contentDiv.style.color = '#0f172a'
    contentDiv.style.fontWeight = '400'
    contentDiv.style.display = 'flex'
    contentDiv.style.flexDirection = 'column'
    contentDiv.style.rowGap = '4px'
    contentDiv.style.minWidth = '260px'
    contentDiv.style.maxWidth = '320px'

    const typeLabel = stop.type === 'pickup' ? 'Pickup' : 'Delivery'
    const city = stop.location?.city || ''
    const state = stop.location?.state || ''
    const date = stop.date || ''
    const time = stop.time || ''
    const facility = stop.facility || ''
    const appointmentNumber = stop.appointmentNumber || ''
    const distanceText = typeof stop.distance === 'number' && stop.distance > 0 ? `${stop.distance} mi` : ''

    const locationText = [city, state].filter(Boolean).join(', ')
    const dateTimeText = [date, time].filter(Boolean).join(' ')

    const badge = document.createElement('div')
    badge.textContent = typeLabel.toUpperCase()
    badge.style.alignSelf = 'flex-start'
    badge.style.padding = '2px 10px'
    badge.style.borderRadius = '999px'
    badge.style.fontSize = '11px'
    badge.style.fontWeight = '600'
    badge.style.letterSpacing = '0.06em'
    badge.style.color = '#ffffff'
    badge.style.backgroundColor = stop.type === 'pickup' ? '#2e7d32' : '#0b4ea2'

    const locationRow = document.createElement('div')
    locationRow.style.fontSize = '12px'
    locationRow.style.fontWeight = '500'
    locationRow.style.marginTop = '2px'
    locationRow.textContent = locationText || 'Location not available'

    const dateTimeRow = document.createElement('div')
    dateTimeRow.style.fontSize = '11px'
    dateTimeRow.style.opacity = '0.8'
    dateTimeRow.textContent = dateTimeText || 'Time not available'

    const metaRow = document.createElement('div')
    metaRow.style.display = 'flex'
    metaRow.style.flexWrap = 'wrap'
    metaRow.style.gap = '6px'
    metaRow.style.marginTop = '4px'

    const makePill = (label: string): HTMLDivElement => {
        const pill = document.createElement('div')
        pill.textContent = label
        pill.style.padding = '2px 8px'
        pill.style.borderRadius = '999px'
        pill.style.fontSize = '10px'
        pill.style.fontWeight = '500'
        pill.style.backgroundColor = '#f3f4f6'
        pill.style.color = '#374151'
        return pill
    }

    if (facility) {
        metaRow.appendChild(makePill(facility))
    }
    if (appointmentNumber) {
        metaRow.appendChild(makePill(`Appt: ${appointmentNumber}`))
    }
    if (distanceText) {
        metaRow.appendChild(makePill(distanceText))
    }

    contentDiv.appendChild(badge)
    contentDiv.appendChild(locationRow)
    contentDiv.appendChild(dateTimeRow)
    if (metaRow.childElementCount > 0) {
        contentDiv.appendChild(metaRow)
    }

    return contentDiv
}

const getLoadStopPosition = (stop: IRoute): google.maps.LatLngLiteral | null => {
    const coords = stop.location?.coordinates?.coordinates
    if (!coords || !Array.isArray(coords) || coords.length < 2) return null
    const [lng, lat] = coords
    if (typeof lat !== 'number' || typeof lng !== 'number') return null
    if (!isValidCoordinate(lat, lng)) return null
    return { lat, lng }
}

export const SelectedTrucksMap: React.FC<SelectedTrucksMapProps> = ({ trucks, activeVin, focusedRow, loadRouteStops }) => {
    const mapRef = React.useRef<HTMLDivElement | null>(null)
    const mapInstanceRef = React.useRef<google.maps.Map | null>(null)
    const markersRef = React.useRef<google.maps.Marker[]>([])
    const infoWindowRef = React.useRef<google.maps.InfoWindow | null>(null)
    const polylinesRef = React.useRef<google.maps.Polyline[]>([])
    const directionsRenderersRef = React.useRef<google.maps.DirectionsRenderer[]>([])

    React.useEffect(() => {
        const styleId = 'hide-infowindow-close-btn'
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style')
            style.id = styleId
            style.textContent = `
                .gm-style-iw-chr { display: none !important; }
                .gm-style-iw { padding: 0 !important; }
                .gm-style-iw-d { overflow: hidden !important; }
            `
            document.head.appendChild(style)
        }
    }, [])

    React.useEffect(() => {
        if (!mapRef.current || !window.google) return

        const google = window.google as typeof window.google

        if (!mapInstanceRef.current) {
            const firstValidTruck = trucks.find((truck) => !isNaN(truck.lat) && !isNaN(truck.lng) && !(truck.lat === 0 && truck.lng === 0))

            const initialCenter = firstValidTruck ? { lat: firstValidTruck.lat, lng: firstValidTruck.lng } : defaultMapCenter

            const mapOptions: google.maps.MapOptions = {
                zoom: 4,
                center: initialCenter,
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: true,
            }

            mapInstanceRef.current = new google.maps.Map(mapRef.current, mapOptions)
        }

        if (!infoWindowRef.current) {
            infoWindowRef.current = new google.maps.InfoWindow({
                disableAutoPan: true,
            })
        }

        markersRef.current.forEach((marker) => marker.setMap(null))
        markersRef.current = []

        polylinesRef.current.forEach((polyline) => polyline.setMap(null))
        polylinesRef.current = []

        directionsRenderersRef.current.forEach((renderer) => renderer.setMap(null))
        directionsRenderersRef.current = []

        const bounds = new google.maps.LatLngBounds()
        let hasValidCoordinates = false

        const trucksToRender = activeVin ? trucks.filter((t) => t.vin === activeVin) : trucks

        const sortedTrucks = sortTruckHistoryByTimestampAsc(trucksToRender)

        const pathsByVin = new Map<string, google.maps.LatLngLiteral[]>()
        const vinIndexCounters = new Map<string, number>()
        const lastPositionByVin = new Map<string, google.maps.LatLngLiteral>()
        const lastMarkerByVin = new Map<string, google.maps.Marker>()

        sortedTrucks.forEach((truck) => {
            const lat = truck.lat
            const lng = truck.lng

            if (!isValidCoordinate(lat, lng)) return

            const position = { lat, lng }
            bounds.extend(position)
            hasValidCoordinates = true

            const previousPosition = lastPositionByVin.get(truck.vin)
            const isStopPoint = arePositionsEqual(previousPosition, position)

            const currentIndex = (vinIndexCounters.get(truck.vin) || 0) + 1
            vinIndexCounters.set(truck.vin, currentIndex)

            let marker: google.maps.Marker

            if (isStopPoint) {
                // Stopped truck at same location → replace previous numbered marker with default red location icon
                const lastMarker = lastMarkerByVin.get(truck.vin)
                if (lastMarker) {
                    lastMarker.setMap(null)
                }

                marker = new google.maps.Marker({
                    position,
                    map: mapInstanceRef.current || undefined,
                })
            } else {
                const vinPath = pathsByVin.get(truck.vin) || []
                vinPath.push(position)
                pathsByVin.set(truck.vin, vinPath)

                const labelNumber = currentIndex
                const isFirstForVin = labelNumber === 1

                marker = new google.maps.Marker({
                    position,
                    map: mapInstanceRef.current || undefined,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: isFirstForVin ? '#beb05eff' : '#28acb1ff',
                        fillOpacity: 1,
                        strokeWeight: 1,
                        strokeColor: '#ffffff',
                        scale: 15,
                    },
                    label: {
                        text: String(labelNumber),
                        color: '#ffffff',
                        fontSize: '15px',
                        fontWeight: 'bold',
                    },
                })
            }

            lastPositionByVin.set(truck.vin, position)
            lastMarkerByVin.set(truck.vin, marker)

            const timestamp = truck.timestamp ? formatToUserLocalTime(truck.timestamp) : ''

            if (timestamp && infoWindowRef.current && mapInstanceRef.current) {
                marker.addListener('mouseover', () => {
                    const contentDiv = createInfoWindowContent(timestamp)

                    infoWindowRef.current!.setContent(contentDiv)
                    infoWindowRef.current!.open(mapInstanceRef.current!, marker)
                })

                marker.addListener('mouseout', () => {
                    infoWindowRef.current?.close()
                })
            }

            markersRef.current.push(marker)
        })

        if (mapInstanceRef.current && Array.isArray(loadRouteStops) && loadRouteStops.length > 0) {
            loadRouteStops.forEach((stop, index) => {
                const position = getLoadStopPosition(stop)
                if (!position) return

                const labelNumber = index + 1

                const marker = new google.maps.Marker({
                    position,
                    map: mapInstanceRef.current || undefined,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: stop.type === 'pickup' ? '#2e7d32' : '#0b4ea2',
                        fillOpacity: 0.9,
                        strokeWeight: 1,
                        strokeColor: '#ffffff',
                        scale: 14,
                    },
                    label: {
                        text: String(labelNumber),
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 'bold',
                    },
                })

                if (infoWindowRef.current && mapInstanceRef.current) {
                    marker.addListener('click', () => {
                        const contentDiv = createLoadStopInfoContent(stop)
                        infoWindowRef.current!.setContent(contentDiv)
                        infoWindowRef.current!.open(mapInstanceRef.current!, marker)
                    })
                }

                markersRef.current.push(marker)
                bounds.extend(position)
                hasValidCoordinates = true
            })
        }

        if (hasValidCoordinates && mapInstanceRef.current) {
            mapInstanceRef.current.fitBounds(bounds)
        }

        if (mapInstanceRef.current && Array.isArray(loadRouteStops) && loadRouteStops.length >= 2) {
            const directionsService = new google.maps.DirectionsService()

            const positions = loadRouteStops
                .map((stop) => getLoadStopPosition(stop))
                .filter((position): position is google.maps.LatLngLiteral => position !== null)

            if (positions.length >= 2) {
                const directionsRenderer = new google.maps.DirectionsRenderer({
                    suppressMarkers: true,
                    polylineOptions: {
                        strokeColor: '#d3902cff',
                        strokeOpacity: 1.0,
                        strokeWeight: 3,
                    },
                })

                directionsRenderer.setMap(mapInstanceRef.current)
                directionsRenderersRef.current.push(directionsRenderer)

                const origin = positions[0]
                const destination = positions[positions.length - 1]
                const waypoints = positions.slice(1, -1).map((location) => ({
                    location,
                    stopover: true,
                }))

                directionsService.route(
                    {
                        origin,
                        destination,
                        waypoints,
                        travelMode: google.maps.TravelMode.DRIVING,
                        optimizeWaypoints: false,
                    },
                    (response, status) => {
                        if (status === google.maps.DirectionsStatus.OK && response) {
                            directionsRenderer.setDirections(response)
                        }
                    }
                )
            }
        }

        if (pathsByVin.size > 0 && mapInstanceRef.current) {
            const directionsService = new google.maps.DirectionsService()

            const MAX_WAYPOINTS = 25
            const MAX_ROUTE_POINTS = MAX_WAYPOINTS + 2

            pathsByVin.forEach((path) => {
                if (path.length < 2) return

                let routePath = path
                if (path.length > MAX_ROUTE_POINTS) {
                    const middlePoints = path.slice(1, -1)
                    const step = middlePoints.length / MAX_WAYPOINTS
                    const sampled: google.maps.LatLngLiteral[] = []
                    for (let i = 0; i < MAX_WAYPOINTS; i += 1) {
                        const index = Math.min(Math.floor(i * step), middlePoints.length - 1)
                        sampled.push(middlePoints[index])
                    }
                    routePath = [path[0], ...sampled, path[path.length - 1]]
                }

                const directionsRenderer = new google.maps.DirectionsRenderer({
                    suppressMarkers: true,
                    polylineOptions: {
                        strokeColor: '#0b4ea2',
                        strokeOpacity: 1.0,
                        strokeWeight: 3,
                    },
                })

                directionsRenderer.setMap(mapInstanceRef.current!)
                directionsRenderersRef.current.push(directionsRenderer)

                const origin = routePath[0]
                const destination = routePath[routePath.length - 1]
                const waypoints = routePath.slice(1, -1).map((location) => ({
                    location,
                    stopover: true,
                }))

                directionsService.route(
                    {
                        origin,
                        destination,
                        waypoints,
                        travelMode: google.maps.TravelMode.DRIVING,
                        optimizeWaypoints: false,
                    },
                    (response, status) => {
                        if (status === google.maps.DirectionsStatus.OK && response) {
                            directionsRenderer.setDirections(response)
                        } else {
                            console.warn('[SelectedTrucksMap] Directions route failed for VIN group, drawing straight polyline. Status:', status)

                            if (mapInstanceRef.current) {
                                const polyline = new google.maps.Polyline({
                                    path,
                                    geodesic: true,
                                    strokeColor: '#0b4ea2',
                                    strokeOpacity: 1.0,
                                    strokeWeight: 3,
                                    map: mapInstanceRef.current,
                                })
                                polylinesRef.current.push(polyline)
                            }
                        }
                    }
                )
            })
        }

        return () => {
            markersRef.current.forEach((marker) => {
                marker.setMap(null)
                google.maps.event.clearInstanceListeners(marker)
            })
            markersRef.current = []

            polylinesRef.current.forEach((polyline) => polyline.setMap(null))
            polylinesRef.current = []

            directionsRenderersRef.current.forEach((renderer) => renderer.setMap(null))
            directionsRenderersRef.current = []

            if (infoWindowRef.current) {
                infoWindowRef.current.close()
                infoWindowRef.current = null
            }

            if (mapInstanceRef.current) {
                google.maps.event.clearInstanceListeners(mapInstanceRef.current)
            }
        }
    }, [trucks, activeVin, loadRouteStops])

    React.useEffect(() => {
        if (!focusedRow || !mapInstanceRef.current || !window.google) return

        const google = window.google as typeof window.google

        const lat = focusedRow.lat
        const lng = focusedRow.lng

        if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return

        const position = { lat, lng }

        mapInstanceRef.current.panTo(position)

        const currentZoom = mapInstanceRef.current.getZoom() || 4
        if (currentZoom < 10) {
            mapInstanceRef.current.setZoom(10)
        }

        if (!infoWindowRef.current) {
            infoWindowRef.current = new google.maps.InfoWindow({
                disableAutoPan: false,
            })
        }

        const timestampText = focusedRow.timestamp ? formatToUserLocalTime(focusedRow.timestamp) : ''

        if (timestampText) {
            const contentDiv = createInfoWindowContent(timestampText)
            infoWindowRef.current!.setContent(contentDiv)
        } else {
            infoWindowRef.current!.setContent('')
        }

        // Try to anchor the InfoWindow to an existing marker at this position
        const tolerance = 1e-6
        const targetMarker = markersRef.current.find((marker) => {
            const markerPos = marker.getPosition()
            if (!markerPos) return false
            const mLat = markerPos.lat()
            const mLng = markerPos.lng()
            return Math.abs(mLat - lat) < tolerance && Math.abs(mLng - lng) < tolerance
        })

        if (targetMarker) {
            infoWindowRef.current!.open(mapInstanceRef.current, targetMarker)
        } else {
            infoWindowRef.current!.setPosition(position)
            infoWindowRef.current!.open(mapInstanceRef.current)
        }
    }, [focusedRow])

    return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
}
